import {
  bindStreamLainDisclosure,
  streamLainAnimationController,
} from '@blocksuite/affine/shared/utils';
import { ArrowRightSmallIcon } from '@blocksuite/icons/rc';
import { assignInlineVars } from '@vanilla-extract/dynamic';
import clsx from 'clsx';
import {
  type ButtonHTMLAttributes,
  type HTMLAttributes,
  type ReactNode,
  type TableHTMLAttributes,
  useId,
  useLayoutEffect,
  useRef,
} from 'react';

import { Checkbox } from '../../ui/checkbox';
import * as styles from './settings-primitives.css';
import { SettingsRailEntry } from './settings-rail';

type DivProps = HTMLAttributes<HTMLDivElement>;

/* ── Section ─────────────────────────────────────────────── */

export interface SettingsSectionFrameProps extends Omit<DivProps, 'title'> {
  title?: ReactNode;
  description?: ReactNode;
  /** Marker next to the title, e.g. a plan chip. */
  badge?: ReactNode;
  action?: ReactNode;
  /** Marks the section as a jump target of the settings navigation. */
  sectionId?: string;
  disabled?: boolean;
  testId?: string;
}

/**
 * A titled block of a settings page. Cards, forms and notes go into its body.
 * With a plain title it is a stop of the page's rail.
 */
export const SettingsSectionFrame = ({
  id,
  title,
  ...props
}: SettingsSectionFrameProps) => {
  const ownId = useId();
  const railTitle = typeof title === 'string' ? title : undefined;
  const anchor = id ?? (railTitle === undefined ? undefined : ownId);
  // One tree for both kinds of title: a title that turns from text into
  // markup must not remount the content of the section.
  return (
    <SettingsRailEntry id={anchor ?? ownId} title={railTitle}>
      <SectionFrame {...props} id={anchor} title={title} />
    </SettingsRailEntry>
  );
};

const SectionFrame = ({
  title,
  description,
  badge,
  action,
  sectionId,
  disabled,
  testId,
  className,
  children,
  ...props
}: SettingsSectionFrameProps) => (
  <section
    {...props}
    className={clsx(styles.section, className)}
    data-setting-section={sectionId}
    data-testid={testId}
    aria-disabled={disabled || undefined}
  >
    {title || badge || action ? (
      <div className={styles.sectionHeader}>
        {typeof title === 'string' ? (
          <h2 className={styles.sectionTitle}>{title}</h2>
        ) : (
          title
        )}
        {badge}
        {action ? <div className={styles.sectionAction}>{action}</div> : null}
      </div>
    ) : null}
    {description ? (
      <div className={styles.sectionDescription}>{description}</div>
    ) : null}
    {children ? <div className={styles.sectionBody}>{children}</div> : null}
  </section>
);

export const settingsSectionTitleClass = styles.sectionTitle;

/* ── Card and rows ───────────────────────────────────────── */

export interface SettingsCardProps extends DivProps {
  padded?: boolean;
  tone?: 'danger' | 'muted';
}

export const SettingsCard = ({
  padded,
  tone,
  className,
  ...props
}: SettingsCardProps) => (
  <div
    {...props}
    className={clsx(styles.card, className)}
    data-padded={padded || undefined}
    data-tone={tone}
  />
);

export interface SettingsRowProps extends Omit<DivProps, 'title' | 'children'> {
  label: ReactNode;
  description?: ReactNode;
  /** Icon or picture in front of the text. */
  lead?: ReactNode;
  /** `media` for an avatar or logo, which brings its own size and shape. */
  leadKind?: 'icon' | 'media';
  /**
   * `stack` puts the control below the text, for inputs that need the full
   * width; `compact` keeps a small control (switch, chip) beside the text on
   * narrow screens too, where `inline` stacks.
   */
  layout?: 'inline' | 'stack' | 'compact';
  disabled?: boolean;
  /** The control this row describes; its id is wired to the label for screen readers. */
  children?:
    | ReactNode
    | ((ids: { labelId: string; descriptionId?: string }) => ReactNode);
}

/** One setting: name and explanation on the left, its control on the right. */
export const SettingsRow = ({
  label,
  description,
  lead,
  leadKind = 'icon',
  layout = 'inline',
  disabled,
  onClick,
  className,
  children,
  ...props
}: SettingsRowProps) => {
  const id = useId();
  const labelId = `${id}-label`;
  const descriptionId = description ? `${id}-description` : undefined;
  return (
    <div
      {...props}
      role="group"
      aria-labelledby={labelId}
      aria-describedby={descriptionId}
      className={clsx(styles.row, className)}
      data-layout={layout}
      data-lead={lead ? true : undefined}
      data-disabled={disabled || undefined}
      data-interactive={onClick ? true : undefined}
      onClick={onClick}
    >
      {lead ? (
        <div className={styles.rowLead} data-kind={leadKind}>
          {lead}
        </div>
      ) : null}
      <div className={styles.rowText}>
        <div id={labelId} className={styles.rowLabel}>
          {label}
        </div>
        {description ? (
          <div id={descriptionId} className={styles.rowDescription}>
            {description}
          </div>
        ) : null}
      </div>
      {children ? (
        <div className={styles.rowControl}>
          {typeof children === 'function'
            ? children({ labelId, descriptionId })
            : children}
        </div>
      ) : null}
    </div>
  );
};

interface SettingsLinkRowBase {
  label: ReactNode;
  description?: ReactNode;
  /** Shown at the end of the row, e.g. an arrow or the "opens elsewhere" mark. */
  icon?: ReactNode;
  className?: string;
  'data-testid'?: string;
}

export type SettingsLinkRowProps = SettingsLinkRowBase &
  (
    | { href: string; target?: string; rel?: string; onClick?: never }
    | { onClick: () => void; href?: never; disabled?: boolean }
  );

/** A row that leads somewhere else; the whole row is the link or the button. */
export const SettingsLinkRow = ({
  label,
  description,
  icon,
  className,
  ...props
}: SettingsLinkRowProps) => {
  const content = (
    <>
      <span className={styles.rowText}>
        <span className={styles.rowLabel}>{label}</span>
        {description ? (
          <span className={styles.linkRowDescription}>{description}</span>
        ) : null}
      </span>
      {icon ? (
        <span className={styles.linkRowIcon} aria-hidden="true">
          {icon}
        </span>
      ) : null}
    </>
  );
  const rowClassName = clsx(styles.row, styles.linkRow, className);
  return props.href === undefined ? (
    <button
      {...props}
      type="button"
      className={rowClassName}
      data-interactive="true"
    >
      {content}
    </button>
  ) : (
    <a {...props} className={rowClassName} data-interactive="true">
      {content}
    </a>
  );
};

/** Cards, hints and notes of one section, one below the other. */
export const SettingsStack = ({ className, ...props }: DivProps) => (
  <div {...props} className={clsx(styles.stack, className)} />
);

export interface SettingsGroupProps extends Omit<DivProps, 'title'> {
  title: ReactNode;
  /** One sentence on what the group holds and how it applies. */
  description?: ReactNode;
  /** At the end of the title line, e.g. the button that adds an item. */
  action?: ReactNode;
}

/**
 * A titled group of cards and hints inside a section, e.g. the tools of one
 * surface below the switches that apply to all of them.
 */
export const SettingsGroup = ({
  title,
  description,
  action,
  className,
  children,
  ...props
}: SettingsGroupProps) => (
  <div {...props} className={clsx(styles.group, className)}>
    <div className={styles.groupHeader}>
      <div className={styles.groupHeading}>
        <h3 className={styles.groupTitle}>{title}</h3>
        {description ? (
          <p className={styles.groupDescription}>{description}</p>
        ) : null}
      </div>
      {action ? <div className={styles.sectionAction}>{action}</div> : null}
    </div>
    {children}
  </div>
);

/** Dependent settings shown indented below the row that enables them. */
export const SettingsSubRows = ({ className, ...props }: DivProps) => (
  <div {...props} className={clsx(styles.subRows, className)} />
);

/* ── Field ───────────────────────────────────────────────── */

export const SettingsForm = ({ className, ...props }: DivProps) => (
  <div {...props} className={clsx(styles.form, className)} />
);

export interface SettingsFieldProps extends Omit<DivProps, 'children'> {
  label: ReactNode;
  /** Short remark at the end of the label line, e.g. "optional". */
  aside?: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  children: (ids: {
    labelId: string;
    describedBy?: string;
    invalid: boolean;
  }) => ReactNode;
}

/** A labelled form control with help and error text below it. */
export const SettingsField = ({
  label,
  aside,
  hint,
  error,
  className,
  children,
  ...props
}: SettingsFieldProps) => {
  const id = useId();
  const labelId = `${id}-label`;
  const hintId = hint ? `${id}-hint` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  const describedBy = [errorId, hintId].filter(Boolean).join(' ') || undefined;
  return (
    <div {...props} className={clsx(styles.field, className)}>
      <div className={styles.fieldLabel}>
        <span id={labelId}>{label}</span>
        {aside ? <span className={styles.fieldAside}>{aside}</span> : null}
      </div>
      {children({ labelId, describedBy, invalid: Boolean(error) })}
      {error ? (
        <div
          id={errorId}
          className={styles.hint}
          data-tone="error"
          role="alert"
        >
          {error}
        </div>
      ) : null}
      {hint ? (
        <div id={hintId} className={styles.hint}>
          {hint}
        </div>
      ) : null}
    </div>
  );
};

export const SettingsHint = ({
  tone,
  className,
  ...props
}: DivProps & { tone?: 'error' }) => (
  <div {...props} className={clsx(styles.hint, className)} data-tone={tone} />
);

/* ── Chip, status, note ──────────────────────────────────── */

export type SettingsChipTone =
  | 'grey'
  | 'blue'
  | 'green'
  | 'orange'
  | 'purple'
  | 'red'
  | 'yellow';

export const SettingsChip = ({
  tone = 'grey',
  className,
  ...props
}: HTMLAttributes<HTMLSpanElement> & { tone?: SettingsChipTone }) => (
  <span {...props} className={clsx(styles.chip, className)} data-tone={tone} />
);

export type SettingsStatusTone =
  | 'neutral'
  | 'success'
  | 'warning'
  | 'error'
  | 'brand';

export const SettingsStatusDot = ({
  tone = 'neutral',
  className,
  ...props
}: HTMLAttributes<HTMLSpanElement> & { tone?: SettingsStatusTone }) => (
  <span
    {...props}
    className={clsx(styles.statusDot, className)}
    data-tone={tone}
  />
);

export interface SettingsNoteProps extends Omit<DivProps, 'title'> {
  icon: ReactNode;
  title?: ReactNode;
  tone?: 'info' | 'warning' | 'error';
  actions?: ReactNode;
}

/** An inline remark that explains a state or a consequence. */
export const SettingsNote = ({
  icon,
  title,
  tone = 'info',
  actions,
  className,
  children,
  ...props
}: SettingsNoteProps) => (
  <div {...props} className={clsx(styles.note, className)} data-tone={tone}>
    <span className={styles.noteIcon} aria-hidden="true">
      {icon}
    </span>
    <div>
      {title ? <div className={styles.noteTitle}>{title}</div> : null}
      {children}
      {actions ? <div className={styles.noteActions}>{actions}</div> : null}
    </div>
  </div>
);

/* ── Stats and meter ─────────────────────────────────────── */

export const SettingsStats = ({ className, ...props }: DivProps) => (
  <div {...props} className={clsx(styles.stats, className)} />
);

export const SettingsStat = ({
  label,
  value,
  detail,
  ...props
}: Omit<SettingsCardProps, 'children'> & {
  label: ReactNode;
  value: ReactNode;
  detail?: ReactNode;
}) => (
  <SettingsCard {...props} className={clsx(styles.stat, props.className)}>
    <div className={styles.statLabel}>{label}</div>
    <div className={styles.statValue}>{value}</div>
    {detail ? <div className={styles.statLabel}>{detail}</div> : null}
  </SettingsCard>
);

export interface SettingsMeterSegment {
  id: string;
  label: ReactNode;
  /** Share of the whole between 0 and 1. */
  share: number;
  color: string;
  /** Marks the segment in the bar, for tests that look for one part. */
  testId?: string;
}

/** One part of a meter; a new share grows or shrinks from the old one. */
const SettingsMeterBar = ({ segment }: { segment: SettingsMeterSegment }) => {
  const ref = useRef<HTMLSpanElement>(null);
  const width = `${Math.max(0, Math.min(1, segment.share)) * 100}%`;
  const shownWidth = useRef(width);
  useLayoutEffect(() => {
    const element = ref.current;
    const from = shownWidth.current;
    shownWidth.current = width;
    if (!element || from === width) return;
    streamLainAnimationController.play(
      element,
      [{ width: from }, { width }],
      'resize'
    );
  }, [width]);
  useLayoutEffect(() => {
    const element = ref.current;
    return () => {
      if (element) streamLainAnimationController.cancel(element);
    };
  }, []);
  return (
    <span
      ref={ref}
      className={styles.meterSegment}
      data-testid={segment.testId}
      style={assignInlineVars({
        [styles.meterShare]: width,
        [styles.meterColor]: segment.color,
      })}
    />
  );
};

/** A usage bar with one coloured segment per part and a legend below. */
export const SettingsMeter = ({
  segments,
  label,
  legend = true,
}: {
  segments: readonly SettingsMeterSegment[];
  label: string;
  /** Off for a bar whose row already names its only part. */
  legend?: boolean;
}) => {
  const used = segments.reduce((sum, segment) => sum + segment.share, 0);
  return (
    <div>
      <div
        className={styles.meter}
        role="meter"
        aria-label={label}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(Math.min(1, used) * 100)}
      >
        {segments.map(segment => (
          <SettingsMeterBar key={segment.id} segment={segment} />
        ))}
      </div>
      {legend ? (
        <div className={styles.legend}>
          {segments.map(segment => (
            <span
              key={segment.id}
              className={styles.legendItem}
              style={assignInlineVars({ [styles.meterColor]: segment.color })}
            >
              {segment.label}
            </span>
          ))}
        </div>
      ) : null}
    </div>
  );
};

/* ── Stepper ─────────────────────────────────────────────── */

export interface SettingsStep {
  id: string;
  /** `active`: in use; `open`: available; `blocked`: waits for an earlier step. */
  state: 'active' | 'open' | 'blocked';
  /** Shown when the step is blocked: what it waits for. */
  requirement?: ReactNode;
  content: ReactNode;
}

/** A numbered sequence where later steps build on earlier ones. */
export const SettingsStepper = ({
  steps,
  ...props
}: Omit<HTMLAttributes<HTMLOListElement>, 'children'> & {
  steps: readonly SettingsStep[];
}) => (
  <ol {...props} className={clsx(styles.steps, props.className)}>
    {steps.map((step, index) => (
      <li key={step.id} className={styles.step} data-state={step.state}>
        <div className={styles.stepRail} aria-hidden="true">
          <span className={styles.stepNumber}>{index + 1}</span>
          <span className={styles.stepLine} />
        </div>
        <div className={styles.stepBody}>
          <SettingsCard tone={step.state === 'blocked' ? 'muted' : undefined}>
            {step.content}
            {step.state === 'blocked' && step.requirement ? (
              <SettingsSubRows>
                <span className={styles.stepRequirement}>
                  {step.requirement}
                </span>
              </SettingsSubRows>
            ) : null}
          </SettingsCard>
        </div>
      </li>
    ))}
  </ol>
);

/* ── Collapsible ─────────────────────────────────────────── */

export interface SettingsCollapsibleProps {
  label: ReactNode;
  /** Shown at the end of the trigger line, e.g. counts of what is inside. */
  summary?: ReactNode;
  defaultOpen?: boolean;
  testId?: string;
  children: ReactNode;
}

/**
 * Optional details inside a card ("Advanced"). Opening and closing run through
 * the shared disclosure motion; the open state lives in the element, so it
 * survives re-renders of the page around it.
 */
export const SettingsCollapsible = ({
  label,
  summary,
  defaultOpen = false,
  testId,
  children,
}: SettingsCollapsibleProps) => {
  const detailsRef = useRef<HTMLDetailsElement>(null);
  useLayoutEffect(() => {
    const details = detailsRef.current;
    if (!details) return;
    return bindStreamLainDisclosure(details);
  }, []);
  return (
    <details
      ref={detailsRef}
      className={styles.collapsible}
      open={defaultOpen}
      data-testid={testId}
    >
      <summary className={styles.collapsibleTrigger}>
        <span
          className={clsx(
            'streamlain-inline-disclosure-chevron',
            styles.collapsibleChevron
          )}
          aria-hidden="true"
        >
          <ArrowRightSmallIcon />
        </span>
        <span>{label}</span>
        {summary ? (
          <span className={styles.collapsibleSummary}>{summary}</span>
        ) : null}
      </summary>
      <div className="streamlain-inline-disclosure-body">{children}</div>
    </details>
  );
};

/* ── Logo choice ─────────────────────────────────────────── */

export interface SettingsLogoPlateProps extends HTMLAttributes<HTMLSpanElement> {
  /** A wordmark needs the wide plate; a square mark fits the default one. */
  shape?: 'square' | 'wide';
  /** Content without brand colours: the plate follows the theme. */
  neutral?: boolean;
}

/**
 * The plate a brand logo sits on. It names nothing itself: the name stands
 * next to it.
 */
export const SettingsLogoPlate = ({
  shape = 'square',
  neutral = false,
  className,
  ...props
}: SettingsLogoPlateProps) => (
  <span
    {...props}
    className={clsx(styles.logoPlate, className)}
    data-shape={shape}
    data-neutral={neutral ? '' : undefined}
    aria-hidden="true"
  />
);

/** A grid of services or providers to pick one from; holds `SettingsLogoTile`s. */
export const SettingsLogoGrid = ({
  className,
  ...props
}: HTMLAttributes<HTMLUListElement>) => (
  <ul {...props} className={clsx(styles.logoGrid, className)} />
);

export interface SettingsLogoTileProps extends Omit<
  ButtonHTMLAttributes<HTMLButtonElement>,
  'name' | 'children'
> {
  /** A `SettingsLogoPlate`. */
  logo: ReactNode;
  name: ReactNode;
  /** Short remark below the name, e.g. the kind of sign-in. */
  meta?: ReactNode;
}

export const SettingsLogoTile = ({
  logo,
  name,
  meta,
  className,
  ...props
}: SettingsLogoTileProps) => (
  <li>
    <button
      type="button"
      {...props}
      className={clsx(styles.logoTile, className)}
    >
      {logo}
      <span className={styles.logoTileName}>{name}</span>
      {meta ? <span className={styles.logoTileMeta}>{meta}</span> : null}
    </button>
  </li>
);

export interface SettingsChosenEntryProps extends Omit<DivProps, 'children'> {
  /** A `SettingsLogoPlate`. */
  logo: ReactNode;
  name: ReactNode;
  meta?: ReactNode;
  /** The way back to the grid. */
  action?: ReactNode;
}

/** Head of the form that follows a `SettingsLogoGrid`: what was picked. */
export const SettingsChosenEntry = ({
  logo,
  name,
  meta,
  action,
  className,
  ...props
}: SettingsChosenEntryProps) => (
  <div {...props} className={clsx(styles.chosenEntry, className)}>
    {logo}
    <div className={styles.chosenEntryText}>
      <span className={styles.chosenEntryName}>{name}</span>
      {meta ? <span className={styles.chosenEntryMeta}>{meta}</span> : null}
    </div>
    {action}
  </div>
);

/* ── Table ───────────────────────────────────────────────── */

/** A plain data table for use inside a card. Mark numeric cells with `data-align="end"`. */
export const SettingsTable = ({
  className,
  ...props
}: TableHTMLAttributes<HTMLTableElement>) => (
  <div className={styles.tableScroll}>
    <table {...props} className={clsx(styles.table, className)} />
  </div>
);

/* ── Selection bar ───────────────────────────────────────── */

export interface SettingsSelectionBarProps extends Omit<DivProps, 'children'> {
  /** Every item the box covers is selected. */
  allSelected: boolean;
  /** Some, but not all, are selected: the box shows a dash. */
  someSelected: boolean;
  /** "3 selected", or the select-all text while nothing is. */
  label: ReactNode;
  selectAllLabel: string;
  onToggleAll: (selectAll: boolean) => void;
  disabled?: boolean;
  /** What to do with the selection; they keep their place while nothing is selected. */
  actions?: ReactNode;
  checkboxTestId?: string;
}

/**
 * The band at the edge of a card with selectable items: one box for all of
 * them, how many are picked and what to do with them.
 */
export const SettingsSelectionBar = ({
  allSelected,
  someSelected,
  label,
  selectAllLabel,
  onToggleAll,
  disabled,
  actions,
  checkboxTestId,
  className,
  ...props
}: SettingsSelectionBarProps) => (
  <div {...props} className={clsx(styles.selectionBar, className)}>
    <Checkbox
      className={styles.selectionBarCheckbox}
      checked={allSelected}
      indeterminate={someSelected && !allSelected}
      disabled={disabled}
      aria-label={selectAllLabel}
      data-testid={checkboxTestId}
      onChange={() => onToggleAll(!allSelected)}
    />
    <span className={styles.selectionBarLabel} aria-live="polite">
      {label}
    </span>
    {actions ? (
      <div className={styles.selectionBarActions}>{actions}</div>
    ) : null}
  </div>
);
